/**
 * 简单的并发队列管理器
 * 通用设计，可以用于任何异步任务
 */
export class ConcurrentQueue<T> {
  private runningTasks = new Set<Promise<void>>();
  private waitingQueue: (() => Promise<void>)[] = [];
  private maxConcurrent: number;

  constructor(maxConcurrent: number) {
    this.maxConcurrent = maxConcurrent;
  }

  /**
   * 添加任务到队列
   */
  async add<R extends T>(taskFn: () => Promise<R>): Promise<R> {
    return new Promise((resolve, reject) => {
      const wrappedTask = async (): Promise<void> => {
        try {
          const result = await taskFn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      if (this.runningTasks.size < this.maxConcurrent) {
        this.executeTask(wrappedTask);
      } else {
        this.waitingQueue.push(wrappedTask);
      }
    });
  }

  /**
   * 执行任务
   */
  private async executeTask(taskFn: () => Promise<void>): Promise<void> {
    const taskPromise = taskFn();
    this.runningTasks.add(taskPromise);

    try {
      await taskPromise;
    } catch {
      // 错误已经在wrappedTask中处理了
    } finally {
      this.runningTasks.delete(taskPromise);
      this.processNextTask();
    }
  }

  /**
   * 处理下一个等待中的任务
   */
  private processNextTask(): void {
    if (this.waitingQueue.length > 0 && this.runningTasks.size < this.maxConcurrent) {
      const nextTask = this.waitingQueue.shift();
      if (nextTask) {
        this.executeTask(nextTask);
      }
    }
  }

  /**
   * 获取当前状态
   */
  getStatus() {
    return {
      running: this.runningTasks.size,
      waiting: this.waitingQueue.length,
      maxConcurrent: this.maxConcurrent,
    };
  }
}
